import cloneDeep from 'lodash.clonedeep';
import { action, computed, makeObservable, observable } from 'mobx';
import { defaultEditableCategory, defaultMenuGroupItemsFilters, defaultValidation } from '../constants/menuGroupEdit';
import { formatItems, isCategoriesWithoutItems } from '../helpers/menuGroupEdit';
import { IMenuGroupEditable, IMenuGroupItemsFilters, IMenuGroupValidation } from '../types/menuGroupEdit';
import { LOCALES, defaulLangTextValidation } from 'src/constants/main';
import { SCREENS } from 'src/constants/screens';
import { appStore } from 'src/mobx/appStore';
import client from 'src/services/client';
import { history } from 'src/services/history';
import { emptyNameValidation, maxCharsValidation } from 'src/utils/validation';
import { IMenuGroup, IMenuGroupCategory, IMenuGroupProduct } from 'src/view/screens/MenuManagementScreen/screens/MenuGroupsScreen/types/menuGroups';
import { localeStore } from '~/mobx/localesStore';
import logger from '~/services/logger';
import { toastSuccess } from '~/utils/toasts';
import { modalStore } from '~/mobx/modalStore';

export class MenuGroupEditStore {
  constructor() {
    makeObservable(this);
  }

  @observable filters: IMenuGroupItemsFilters = defaultMenuGroupItemsFilters;
  @observable editableMenuGroup: IMenuGroupEditable = defaultEditableCategory;
  @observable validation: IMenuGroupValidation = cloneDeep(defaultValidation);
  @observable draggingCategory: IMenuGroupCategory | null = null;
  @observable draggingItem: IMenuGroupProduct | null = null;
  @observable selectedCategoryId: number | null = null;
  @observable loadingOnFetch: boolean = false;
  @observable loadingOnSave: boolean = false;
  @observable isEdited: boolean = false;

  @computed get selectedCategory() {
    return this.editableMenuGroup.categories.find((category) => category.id === this.selectedCategoryId);
  }

  @action setFilters = (filters: Partial<IMenuGroupItemsFilters>) => {
    this.filters = { ...this.filters, ...filters };
  };

  @action setSelectedCategoryId = (categoryId: number) => {
    this.selectedCategoryId = categoryId;
  };

  @action changeMenuGroupName = (value: string, currentLanguage: LOCALES) => {
    this.editableMenuGroup.name = { ...this.editableMenuGroup.name, [currentLanguage]: value };
    this.isEdited = true;

    // update validation
    if (!this.validation.name.enValid || !this.validation.name.arValid) this.validateName();
  };

  @action changeMenuGroupCategories = (categories: IMenuGroupCategory[]) => {
    this.editableMenuGroup.categories = categories.map((category) => ({ ...category, items: formatItems(category) }));

    if (categories.length && !this.selectedCategory) this.setSelectedCategoryId(categories[0].id);
    if (categories.length) this.validateCategories();

    this.isEdited = true;
  };

  @action changeMenuGroupCategoryItems = (items: IMenuGroupProduct[]) => {
    // set empty sizes if they are not available in item
    const itemsWithFormattedSizes = items.map((item) => ({ ...item, sizes: item.is_size ? item.sizes : [] }));

    this.updateItems(this.selectedCategory?.id, itemsWithFormattedSizes);
    this.validateItems();

    this.isEdited = true;
  };

  @action changeItemPrice = (item: IMenuGroupProduct, categoryId: number | undefined, price: string) => {
    const category = this.editableMenuGroup.categories.find((_category) => _category.id === categoryId);
    const updatedItems = category?.items.map((_item) => {
      if (_item.id === item.id)
        return { ...item, menu_group_price: price, ...(modalStore.modifiers?.length && { modifiers: [...modalStore.modifiers] }) };
      return _item;
    });

    if (category && updatedItems) {
      this.updateItems(categoryId, updatedItems);
    }

    this.isEdited = true;
  };

  @action excludeCategoryItem = (categoryId: number | undefined, itemId: number | undefined) => {
    const category = this.editableMenuGroup.categories.find((_category) => _category.id === categoryId);
    const updatedItems = category?.items?.filter((item) => item.id !== itemId);

    if (category && updatedItems) {
      this.updateItems(category.id, updatedItems);
    }

    this.isEdited = true;
  };

  @action excludeCategory = (categoryId: number, index: number) => {
    const updatedCategories = this.editableMenuGroup.categories.filter((category) => category.id !== categoryId);
    this.editableMenuGroup.categories = updatedCategories;

    // Select other category if excluded was selected
    if (categoryId === this.selectedCategoryId && updatedCategories.length) {
      const categoryForSelection = updatedCategories[index ? index - 1 : index] || updatedCategories[0];
      this.setSelectedCategoryId(categoryForSelection.id);
    }

    this.isEdited = true;
  };

  @action copyMenuGroupDetailsForEditing = async (menuGroupId: number | string) => {
    await this.fetchMenuGroup(menuGroupId);
    this.editableMenuGroup.name = {
      en: '',
      ar: '',
    };
    this.validate();

    this.isEdited = true;
  };

  @action fetchMenuGroup = async (id: number | string) => {
    try {
      this.loadingOnFetch = true;
      const menuGroup: IMenuGroup = await client.get(`v3/menu_groups/${id}`, { headers: { 'Accept-Language': 'en,ar' } });

      this.editableMenuGroup = {
        ...menuGroup,
        categories: menuGroup.categories.map((category) => ({ ...category, items: formatItems(category) })),
      };
      this.loadingOnFetch = false;

      if (menuGroup.categories.length) {
        this.selectedCategoryId = menuGroup.categories[0].id;
      }
    } catch (error: any) {
      this.loadingOnFetch = false;
      logger.error({ error, place: 'MenuGroupEditStore fetchMenuGroup' });
    }
  };

  @action createMenuGroup = async () => {
    try {
      this.loadingOnSave = true;

      await client.post('v3/menu_groups', this.params);

      this.loadingOnSave = false;
      this.isEdited = false;
      history.push(SCREENS.MENU_GROUPS());

      toastSuccess({ message: localeStore.t('toast_menu_group_changes_saved') });
    } catch (error: any) {
      this.loadingOnSave = false;
      logger.error({ error, place: 'MenuGroupEditStore createMenuGroup', showModal: true });
    }
  };

  @action updateMenuGroup = async (id: number | string) => {
    try {
      this.loadingOnSave = true;

      await client.put(`v3/menu_groups/${id}`, this.params);

      this.loadingOnSave = false;
      this.isEdited = false;
      history.push(SCREENS.MENU_GROUPS());

      toastSuccess({ message: localeStore.t('toast_menu_group_changes_saved') });
    } catch (error: any) {
      this.loadingOnSave = false;
      logger.error({ error, place: 'MenuGroupEditStore updateMenuGroup', showModal: true });
    }
  };

  private get params() {
    return {
      name: this.editableMenuGroup.name,
      categories: this.editableMenuGroup.categories.map((category) => {
        return {
          id: category.id,
          name: category.name,
          items: category.items.map((item) => ({
            id: item.id,
            modifiers: item.modifiers.map((modifier) => {
              return {
                ...modifier,
                options: modifier.options.map((option) => {
                  const { menu_group_price, ...rest } = option;
                  return { ...rest, price: appStore.settings?.is_taker360 && !item.sizes.length ? menu_group_price ?? '' : undefined };
                }),
              };
            }),
            price: appStore.settings?.is_taker360 && !item.sizes.length ? item.menu_group_price : undefined,
          })),
        };
      }),
    };
  }

  @action updateItems = (categoryId: number | undefined, items: IMenuGroupProduct[]) => {
    this.editableMenuGroup.categories = this.editableMenuGroup.categories.map((_category) => {
      if (_category.id !== categoryId) return _category;
      return {
        ..._category,
        items,
      };
    });
  };

  @action public validate = () => {
    this.validation = cloneDeep(defaultValidation);

    this.validateName(true);
    this.validateCategories();
  };

  @action public validateName = (alreadyReset?: boolean) => {
    if (!alreadyReset) this.validation.name = cloneDeep(defaulLangTextValidation);

    [this.validation.name, this.validation.valid] = emptyNameValidation(this.editableMenuGroup.name, this.validation.name, this.validation.valid);

    if (this.validation.name.enValid) {
      [this.validation.name.enValid, this.validation.valid, this.validation.name.enErrorText] = maxCharsValidation(
        this.editableMenuGroup.name.en,
        this.validation.name.enValid,
        this.validation.valid,
        120
      );
    }

    if (this.validation.name.arValid) {
      [this.validation.name.arValid, this.validation.valid, this.validation.name.arErrorText] = maxCharsValidation(
        this.editableMenuGroup.name.ar,
        this.validation.name.arValid,
        this.validation.valid,
        120
      );
    }
  };

  @action public validateCategories = () => {
    if (!this.editableMenuGroup.categories.length) {
      this.validation.categories = false;
      this.validation.valid = false;
    } else {
      this.validation.categories = true;
      this.validateItems();
    }
  };

  @action public validateItems = () => {
    this.validation.items = !isCategoriesWithoutItems(this.editableMenuGroup.categories);

    if (!this.validation.items) {
      this.validation.valid = false;
    }
  };

  @action changeCategoriesSorting = (activeId: number, overId: number | null) => {
    const initCategory = this.editableMenuGroup.categories.find((category) => category.id === activeId);
    const swapCategory = this.editableMenuGroup.categories.find((category) => category.id === overId);

    this.editableMenuGroup.categories = this.editableMenuGroup.categories.map((category) => {
      if (category.id === activeId && swapCategory) return swapCategory;
      if (category.id === overId && initCategory) return initCategory;
      return category;
    });

    this.setDraggingCategory(null);
  };

  @action setDraggingCategory = (id: number | null) => {
    const category = this.editableMenuGroup.categories.find((_category) => _category.id === id);
    this.draggingCategory = category || null;
  };

  @action changeCategoryItemsSorting = (activeId: number, overId: number | null) => {
    const { items } = this.selectedCategory || {};

    if (!items) return;

    const initItem = items.find((item) => item.id === activeId);
    const swapItem = items.find((item) => item.id === overId);

    const updatedItems = items.map((item) => {
      if (item.id === activeId && swapItem) return swapItem;
      if (item.id === overId && initItem) return initItem;
      return item;
    });
    this.updateItems(this.selectedCategory?.id, updatedItems);

    this.setDraggingItem(null);
  };

  @action setDraggingItem = (id: number | null) => {
    const item = this.selectedCategory?.items?.find((_item) => _item.id === id);
    this.draggingItem = item || null;
  };

  @action clearEditing = () => {
    this.editableMenuGroup = defaultEditableCategory;
    this.filters = defaultMenuGroupItemsFilters;
    this.validation = cloneDeep(defaultValidation);
    this.isEdited = false;
  };
}

const menuGroupEditStore = new MenuGroupEditStore();

export default menuGroupEditStore;
